import { Container, Sprite, Assets, Text, TextStyle } from 'pixi.js';
import gsap from 'gsap';
import { BaseScene } from './BaseScene';
import { UIButton } from '../components/UIButton';
import { COLORS, FONTS, SPACING } from '../constants/theme';
import { LAYOUT_STYLES } from '../constants/layoutStyles';
import type { LayoutMetrics } from '../LayoutManager';
import type { AIDifficulty } from '@/types';

export interface AIDifficultySceneOptions {
  onSelectDifficulty: (difficulty: AIDifficulty) => void;
  onBack: () => void;
}

/**
 * AI difficulty selection scene - simple scene with 3 difficulty buttons
 */
export class AIDifficultyScene extends BaseScene {
  private metrics: LayoutMetrics;
  private options: AIDifficultySceneOptions;

  // Background and title
  private backgroundSprite: Sprite | null = null;

  // Root layout
  private rootLayout: Container | null = null;

  // Buttons
  private backButton: UIButton | null = null;

  constructor(metrics: LayoutMetrics, options: AIDifficultySceneOptions) {
    super();
    this.metrics = metrics;
    this.options = options;
    this.build();
  }

  protected build(): void {
    // Empty - content built in onEnter after assets load
  }

  private async loadAssets(): Promise<void> {
    try {
      const backgroundTexture = await Assets.load('/assets/background.png');

      this.backgroundSprite = new Sprite(backgroundTexture);
      this.backgroundSprite.anchor.set(0, 0);
    } catch (error) {
      console.error('Failed to load assets:', error);
    }
  }

  private buildContent(): void {
    this.cleanupContent();

    const { width, height } = this.metrics;

    // Background
    if (this.backgroundSprite) {
      this.backgroundSprite.width = width;
      this.backgroundSprite.height = height;
      this.container.addChild(this.backgroundSprite);
    }

    // Root layout - centered column
    this.rootLayout = new Container({
      layout: {
        ...LAYOUT_STYLES.centeredScreen(width, height),
        flexDirection: 'column',
        gap: SPACING.LG,
      },
    });
    this.container.addChild(this.rootLayout);

    // Title text
    const titleStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: Math.min(48, width * 0.08),
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    const title = new Text({ text: 'Select Difficulty', style: titleStyle });
    title.layout = true;
    this.rootLayout.addChild(title);

    // Subtitle
    const subtitleStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: Math.min(20, width * 0.04),
      fill: COLORS.TEXT_SECONDARY,
    });
    const subtitle = new Text({ text: 'Choose your AI opponent strength', style: subtitleStyle });
    subtitle.layout = true;
    this.rootLayout.addChild(subtitle);

    // Spacer
    const spacer = new Container({ layout: { height: SPACING.LG } });
    this.rootLayout.addChild(spacer);

    // Difficulty buttons container
    const buttonWidth = Math.min(280, width * 0.6);
    const buttonHeight = 60;

    // Easy button (green)
    const easyWrapper = new Container({ layout: { width: buttonWidth, height: buttonHeight } });
    const easyBtn = new UIButton({
      text: 'Easy',
      width: buttonWidth,
      height: buttonHeight,
      variant: 'success',
      onClick: () => this.selectDifficulty('easy'),
    });
    easyBtn.x = buttonWidth / 2;
    easyBtn.y = buttonHeight / 2;
    easyWrapper.addChild(easyBtn);
    this.rootLayout.addChild(easyWrapper);

    // Medium button (yellow)
    const mediumWrapper = new Container({ layout: { width: buttonWidth, height: buttonHeight } });
    const mediumBtn = new UIButton({
      text: 'Medium',
      width: buttonWidth,
      height: buttonHeight,
      variant: 'warning',
      onClick: () => this.selectDifficulty('medium'),
    });
    mediumBtn.x = buttonWidth / 2;
    mediumBtn.y = buttonHeight / 2;
    mediumWrapper.addChild(mediumBtn);
    this.rootLayout.addChild(mediumWrapper);

    // Hard button (red)
    const hardWrapper = new Container({ layout: { width: buttonWidth, height: buttonHeight } });
    const hardBtn = new UIButton({
      text: 'Hard',
      width: buttonWidth,
      height: buttonHeight,
      variant: 'danger',
      onClick: () => this.selectDifficulty('hard'),
    });
    hardBtn.x = buttonWidth / 2;
    hardBtn.y = buttonHeight / 2;
    hardWrapper.addChild(hardBtn);
    this.rootLayout.addChild(hardWrapper);

    // Spacer
    const spacer2 = new Container({ layout: { height: SPACING.XL } });
    this.rootLayout.addChild(spacer2);

    // Back button
    const backWidth = 120;
    const backWrapper = new Container({ layout: { width: backWidth, height: 48 } });
    this.backButton = new UIButton({
      text: 'Back',
      width: backWidth,
      variant: 'secondary',
      onClick: () => this.handleBack(),
    });
    this.backButton.x = backWidth / 2;
    this.backButton.y = 24;
    backWrapper.addChild(this.backButton);
    this.rootLayout.addChild(backWrapper);
  }

  private cleanupContent(): void {
    this.container.removeChildren();
    this.rootLayout = null;
    this.backButton = null;
  }

  private selectDifficulty(difficulty: AIDifficulty): void {
    this.cleanupContent();
    this.options.onSelectDifficulty(difficulty);
  }

  private handleBack(): void {
    this.cleanupContent();
    this.options.onBack();
  }

  async onEnter(): Promise<void> {
    super.onEnter();

    await this.loadAssets();
    this.buildContent();

    this.container.alpha = 0;
    gsap.to(this.container, { alpha: 1, duration: 0.3 });
  }

  onExit(): void {
    super.onExit();
    this.cleanupContent();
  }

  onResize(metrics: LayoutMetrics): void {
    this.metrics = metrics;
    if (this.isActive) {
      this.buildContent();
    }
  }

  dispose(): void {
    this.cleanupContent();
    super.dispose();
  }
}
